import { SearchableItem } from '@core/search';
import {
  PERMISSION_CATALOG,
  PERMISSION_MODULES,
  PERMISSION_POINTERS,
  PermissionModule,
} from '../permission-catalog';

export interface PermissionPayload {
  module: PermissionModule;
  permissionKey: string;
}

const MODULE_LABEL_KEY: Record<PermissionModule, string> = PERMISSION_MODULES.reduce(
  (acc, m) => ({ ...acc, [m.module]: m.labelKey }),
  {} as Record<PermissionModule, string>
);

const ROOT_BREADCRUMB_KEY = 'USER_PERMISSIONS.V2.SEARCH.GROUP_LABEL';

/**
 * Build the SearchableItem list for the permissions domain.
 * Per-permission keywords are loaded from JSON and overlaid here, falling
 * back to an empty array if the key is missing.
 *
 * `PERMISSION_POINTERS` are indexed alongside the real permissions: they are rows in the
 * same list that point at another place instead of toggling a right (currently the
 * „Personalakte" summary row, ticket F-10). They carry the same `{module, key}` shape, so
 * the action handler (switch module → scroll to `perm-row-{module}-{key}` → pulse) works
 * without a special case.
 */
export function buildPermissionsIndex(
  keywordsByKey: Record<string, string[]>
): SearchableItem<PermissionPayload>[] {
  return [...PERMISSION_CATALOG, ...PERMISSION_POINTERS].map((p) => {
    const lookupKey = `${p.module}.${p.key}`;
    const tags: string[] = [];
    if (p.isCritical) tags.push('critical');
    if (p.isSensitive) tags.push('sensitive');
    return {
      id: `permissions:${lookupKey}`,
      providerId: 'permissions',
      title: p.labelKey,
      description: p.descriptionKey,
      keywords: keywordsByKey[lookupKey] ?? [],
      breadcrumb: [ROOT_BREADCRUMB_KEY, MODULE_LABEL_KEY[p.module]],
      icon: p.icon,
      tags,
      payload: {
        module: p.module,
        permissionKey: p.key,
      },
    };
  });
}
